import { useMemo } from 'react';
import { getRemotionEnvironment, Html5Audio, Html5Video, OffthreadVideo } from 'remotion';
import { Video as MediaBunnyRenderer } from '@remotion/media';
import { useRemotionEnvVariables } from '../../../MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionEnvVariables';
import HlsVideo from '../../HlsVideo';
import { VideoElement } from '@/App/remotion/MainPlayer/types/Element/VideoElement';
import { useSegmentTransitionVolume } from '@/App/remotion/components/SegmentTransitionWrapper/SegmentTransitionVolumeContext';
import { useRemotionUsage } from '@/App/remotion/MainPlayer/RemotionUsageContext/RemotionUsageContext';

enum VideoRendererType {
  HTML5_AUDIO = 'HTML5_AUDIO',
  MEDIA_BUNNY = 'MEDIA_BUNNY',
  OFFTHREAD = 'OFFTHREAD',
  HLS = 'HLS',
  HTML5_VIDEO = 'HTML5_VIDEO'
}

export const VideoRenderer = ({
  element,
  ignoreEndAt,
  disableMediaFragmentHints,
  handleVideoLoad
}: {
  element: VideoElement;
  ignoreEndAt?: boolean;
  disableMediaFragmentHints?: boolean;
  handleVideoLoad: () => void;
}) => {
  const { details, fromFrame, toFrame, style } = element;
  const isPreview = !getRemotionEnvironment().isRendering;
  const { useMediaBunnyVideoRenderer, isGeneratingThumbnail } = useRemotionEnvVariables();

  const { useNewVideoTag } = useRemotionUsage();

  const videoSrc: string = useMemo(() => {
    if (isPreview && details.preview) {
      return details.preview;
    }
    if (isGeneratingThumbnail && details.preview) {
      return details.preview;
    }

    return details.src;
  }, [details.src, isPreview, details.preview, isGeneratingThumbnail]);

  const isAudio = useMemo(() => {
    return new URL(videoSrc).pathname.endsWith('.mp3');
  }, [videoSrc]);

  const videoType = useMemo(() => {
    /**
     * If We use <Audio /> in preview, then as soon as speaker switches Audio is lost, Hence we are using <Video /> tag in preview
     * If We don't use <Audio /> tag during rendering, then the render fails internal server issue,
     * Hence in Render we're using Audio tag but in preview we
     */
    if (!isPreview && isAudio) {
      return VideoRendererType.HTML5_AUDIO;
    }

    if (useNewVideoTag || useMediaBunnyVideoRenderer) {
      return VideoRendererType.MEDIA_BUNNY;
    }

    if (!isPreview) {
      return VideoRendererType.OFFTHREAD;
    }

    if (details.src.includes('hls')) {
      return VideoRendererType.HLS;
    }

    return VideoRendererType.HTML5_VIDEO;
  }, [isPreview, isAudio, useMediaBunnyVideoRenderer, details.src, useNewVideoTag]);

  const endAt = useMemo(() => {
    if (ignoreEndAt) {
      return undefined;
    }

    return toFrame;
  }, [ignoreEndAt, toFrame]);

  const baseVolume = useMemo(() => {
    return element.details.muted ? 0 : Math.round(details.volume[0].value) / 100;
  }, [element.details.muted, details.volume]);

  const segmentTransitionVolumeMultiplier = useSegmentTransitionVolume();
  const volume = baseVolume * segmentTransitionVolumeMultiplier;

  switch (videoType) {
    case VideoRendererType.HTML5_AUDIO:
      return (
        <Html5Audio
          crossOrigin="anonymous"
          trimBefore={fromFrame}
          trimAfter={endAt}
          src={disableMediaFragmentHints ? `${videoSrc}#disable` : videoSrc}
          volume={volume}
          style={{ pointerEvents: 'none', ...style }}
          onError={e => console.error(e)}
          pauseWhenBuffering={!isAudio}
          onCanPlayThrough={handleVideoLoad}
        />
      );
    case VideoRendererType.MEDIA_BUNNY:
      return (
        <MediaBunnyRenderer
          trimBefore={fromFrame}
          trimAfter={endAt}
          src={disableMediaFragmentHints ? `${videoSrc}#disable` : videoSrc}
          volume={volume}
          style={{ pointerEvents: 'none', ...style }}
        />
      );
    case VideoRendererType.OFFTHREAD:
      return (
        <OffthreadVideo
          crossOrigin="anonymous"
          trimBefore={fromFrame}
          trimAfter={endAt}
          src={disableMediaFragmentHints ? `${videoSrc}#disable` : videoSrc}
          volume={volume}
          style={{ pointerEvents: 'none', ...style }}
          onError={e => console.error(e)}
          pauseWhenBuffering={!isAudio}
        />
      );
    case VideoRendererType.HLS:
      return (
        <HlsVideo
          crossOrigin="anonymous"
          trimBefore={fromFrame}
          trimAfter={endAt}
          src={disableMediaFragmentHints ? `${videoSrc}#disable` : videoSrc}
          volume={volume}
          style={{ pointerEvents: 'none', ...style }}
          onError={e => console.error(e)}
          pauseWhenBuffering={!isAudio}
          onCanPlayThrough={handleVideoLoad}
        />
      );
    case VideoRendererType.HTML5_VIDEO:
      return (
        <Html5Video
          crossOrigin="anonymous"
          trimBefore={fromFrame}
          trimAfter={endAt}
          src={disableMediaFragmentHints ? `${videoSrc}#disable` : videoSrc}
          volume={volume}
          style={{ pointerEvents: 'none', ...style }}
          onError={e => console.error(e)}
          pauseWhenBuffering={!isAudio}
          onCanPlayThrough={handleVideoLoad}
        />
      );
  }
};
